import { useCallback, useMemo } from 'react';
import type { ProcessedReportInfo } from '../../types/reporting';
import type { ProcessedMetadata } from '../../utils/metadata/main-metadata';
import type { GridRowAction } from '../GridRowActions/GridRowActions';
import { useRecordNavigator } from '../../hooks/useRecordNavigator';
import { prefetchRecordDestinationIfAbsent } from '../../utils/recordDestinationPrefetch';

export type ReportRecordRow = Record<string, unknown>;

function recordId(value: unknown): string | number | null {
  return typeof value === 'string' || typeof value === 'number' ? value : null;
}

export function useReportRecordInteractions(
  report: ProcessedReportInfo | null | undefined,
  metadata: ProcessedMetadata | undefined,
): {
  navigateToRecord: (tableName: string, id: string | number, label?: string) => void;
  onRowClick?: (row: ReportRecordRow) => void;
  prefetchRowRecord?: (row: ReportRecordRow) => Promise<void>;
  rowActions: GridRowAction<ReportRecordRow>[];
} {
  const { openRecord } = useRecordNavigator();
  const openTableRecord = useCallback((tableName: string, id: string | number, label?: unknown) => {
    const route = metadata?.routeData?.tableToRouteName?.[tableName] ?? tableName;
    const tableLabel = metadata?.tableData?.byName?.[tableName]?.ui_label ?? tableName.replace(/_/g, ' ');
    openRecord({
      id,
      table: route,
      tableName,
      label: typeof label === 'string' && label.trim() ? label : `${tableLabel} #${id}`,
    });
  }, [metadata?.routeData?.tableToRouteName, metadata?.tableData?.byName, openRecord]);

  const navigateToRecord = useCallback((tableName: string, id: string | number, label?: string) => {
    openTableRecord(tableName, id, label);
  }, [openTableRecord]);

  const prefetchRecord = useCallback((tableName: string, id: string | number): Promise<void> => {
    return prefetchRecordDestinationIfAbsent({ tableName, id }, metadata);
  }, [metadata]);

  // Submission rows, and REG-954 send-failure rows (one per failed funder
  // email), open the deal's merchant like the Submissions list does.
  const opensMerchant = report?.primary_table_name === 'submission'
    || report?.primary_table_name === 'submission_send_failure';

  const onRowClick = useMemo(() => {
    if (!report?.primary_table_name) return undefined;
    if (opensMerchant) {
      return (row: ReportRecordRow) => {
        const merchantId = recordId(row.merchant);
        if (merchantId == null) return;
        openTableRecord('merchant', merchantId, row.merchant_label ?? row.business_name);
      };
    }

    return (row: ReportRecordRow) => {
      const id = recordId(row.id);
      if (id == null) return;
      openTableRecord(report.primary_table_name!, id);
    };
  }, [openTableRecord, opensMerchant, report?.primary_table_name]);

  const prefetchRowRecord = useMemo(() => {
    if (!report?.primary_table_name) return undefined;
    if (opensMerchant) {
      return (row: ReportRecordRow) => {
        const merchantId = recordId(row.merchant);
        return merchantId == null ? Promise.resolve() : prefetchRecord('merchant', merchantId);
      };
    }
    return (row: ReportRecordRow) => {
      const id = recordId(row.id);
      return id == null ? Promise.resolve() : prefetchRecord(report.primary_table_name!, id);
    };
  }, [opensMerchant, prefetchRecord, report?.primary_table_name]);

  const rowActions = useMemo<GridRowAction<ReportRecordRow>[]>(() => {
    const submissionIdField = report?.primary_table_name === 'submission'
      ? 'id'
      : report?.primary_table_name === 'merchant'
        ? 'submission_id'
        : report?.primary_table_name === 'submission_send_failure'
          ? 'submission'
          : null;
    if (!submissionIdField) return [];

    return [{
      id: 'open-submission',
      label: 'Open submission',
      isVisible: (row) => recordId(row[submissionIdField]) != null,
      ariaLabel: (row) => `Open submission #${String(row.submission_number ?? row[submissionIdField])}`,
      onPrefetch: (row) => {
        const id = recordId(row[submissionIdField]);
        return id == null ? undefined : prefetchRecord('submission', id);
      },
      onSelect: (row) => {
        const id = recordId(row[submissionIdField]);
        if (id == null) return;
        openTableRecord('submission', id, row.submission_label);
      },
    }];
  }, [openTableRecord, prefetchRecord, report?.primary_table_name]);

  return { navigateToRecord, onRowClick, prefetchRowRecord, rowActions };
}
